import React, { useEffect, useMemo, useState } from "react";
import {
    SovosAutocomplete,
    SovosBox,
    SovosButton,
    SovosDialog,
    SovosDialogActions,
    SovosDialogContent,
    SovosDialogTitle,
    SovosInputAdornment,
    SovosTextField,
    SovosTooltip,
    SovosTypography,
} from "mosaic-react";
import { CircularProgress } from "@mui/material";
import AiConfidencePill from "./AiConfidencePill";

const isValidFiscalYear = (value) => /^\d{4}$/.test(value);

const digitsOf = (value) => String(value ?? "").replace(/\D/g, "").slice(0, 4);

const yearOf = (option) => {
    if (typeof option === "string") return option;
    return option?.year || "";
};

const AiRationalePill = ({ score, rationale }) => {
    const pill = (
        <span style={{ display: "inline-flex" }}>
            <AiConfidencePill score={score} title="" />
        </span>
    );
    if (!rationale) return pill;
    return (
        <SovosTooltip placement="top" title={rationale}>
            {pill}
        </SovosTooltip>
    );
};

const ProceedFiscalYearDialog = ({
    open,
    fiscalYears = [],
    suggestedFiscalYear = "",
    suggestedScore,
    suggestedRationale = "",
    suggesting = false,
    confirming = false,
    i18n,
    onCancel,
    onConfirm,
}) => {
    const [fiscalYear, setFiscalYear] = useState("");

    useEffect(() => {
        if (!open) return;
        setFiscalYear(isValidFiscalYear(suggestedFiscalYear) ? String(suggestedFiscalYear) : "");
    }, [open, suggestedFiscalYear]);

    const options = useMemo(() => {
        const years = new Set((fiscalYears || []).filter((year) => isValidFiscalYear(year)));
        if (isValidFiscalYear(suggestedFiscalYear)) {
            years.add(String(suggestedFiscalYear));
        }
        return Array.from(years)
            .sort((a, b) => Number(b) - Number(a))
            .map((year) => ({
                year,
                score:
                    suggestedScore != null && String(year) === String(suggestedFiscalYear)
                        ? suggestedScore
                        : null,
            }));
    }, [fiscalYears, suggestedFiscalYear, suggestedScore]);

    const selectedOption = options.find((option) => option.year === fiscalYear) || null;
    const autocompleteValue = selectedOption || fiscalYear || null;
    const showPill = !suggesting && selectedOption?.score != null;

    const canConfirm = isValidFiscalYear(fiscalYear) && !confirming && !suggesting;

    const handleYearChange = (_event, newValue) => {
        setFiscalYear(digitsOf(yearOf(newValue)));
    };

    const handleInputChange = (_event, newInputValue, reason) => {
        if (reason !== "input") return;
        setFiscalYear(digitsOf(newInputValue));
    };

    const handleConfirm = () => {
        if (!canConfirm) return;
        onConfirm?.(fiscalYear);
    };

    return (
        <SovosDialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
            <SovosDialogTitle showCloseButton onClose={onCancel}>
                {i18n`Select Fiscal Year`}
            </SovosDialogTitle>
            <SovosDialogContent>
                <SovosTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {i18n`Enter the fiscal year you want to map data into. This determines the target period in the repository.`}
                </SovosTypography>
                <SovosAutocomplete
                    value={autocompleteValue}
                    inputValue={fiscalYear}
                    onChange={handleYearChange}
                    onInputChange={handleInputChange}
                    options={options}
                    freeSolo
                    selectOnFocus
                    clearOnBlur={false}
                    disableClearable
                    disabled={suggesting || confirming}
                    forcePopupIcon
                    getOptionLabel={(option) => yearOf(option)}
                    isOptionEqualToValue={(option, value) => yearOf(option) === yearOf(value)}
                    renderOption={(props, option) => {
                        const { key, ...optionProps } = props;
                        return (
                            <li
                                key={key}
                                {...optionProps}
                                style={{
                                    ...optionProps.style,
                                    display: "flex",
                                    width: "100%",
                                }}
                            >
                                <SovosBox
                                    sx={{
                                        alignItems: "center",
                                        boxSizing: "border-box",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        pr: 5,
                                        width: "100%",
                                    }}
                                >
                                    <span>{option.year}</span>
                                    {option.score != null ? (
                                        <AiRationalePill
                                            score={option.score}
                                            rationale={suggestedRationale}
                                        />
                                    ) : null}
                                </SovosBox>
                            </li>
                        );
                    }}
                    renderInput={(params) => (
                        <SovosTextField
                            {...params}
                            label={i18n`Fiscal Year`}
                            inputProps={{
                                ...params.inputProps,
                                inputMode: "numeric",
                                maxLength: 4,
                            }}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {suggesting ? (
                                            <SovosInputAdornment position="end">
                                                <CircularProgress
                                                    size={16}
                                                    aria-label={i18n`Loading`}
                                                />
                                            </SovosInputAdornment>
                                        ) : null}
                                        {showPill && (
                                            <SovosInputAdornment position="end">
                                                <AiRationalePill
                                                    score={suggestedScore}
                                                    rationale={suggestedRationale}
                                                />
                                            </SovosInputAdornment>
                                        )}
                                        {params.InputProps?.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
            </SovosDialogContent>
            <SovosDialogActions>
                <SovosButton variant="text" onClick={onCancel} disabled={confirming}>
                    {i18n`Cancel`}
                </SovosButton>
                <SovosButton variant="contained" onClick={handleConfirm} disabled={!canConfirm}>
                    {i18n`Proceed`}
                </SovosButton>
            </SovosDialogActions>
        </SovosDialog>
    );
};

export default ProceedFiscalYearDialog;
